import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Dialog,
  DialogContent,
  TextField,
} from "@mui/material";
import { postRescript } from "../api";
import { RescriptResponse } from "../types";
import { useToast } from "../../../components/Toast";

interface ImageryRescriptToolProps {
  patientId: string;
  sessionId?: string;
  getToken: () => Promise<string | null>;
}

export function ImageryRescriptTool({ patientId, sessionId, getToken }: ImageryRescriptToolProps) {
  const { t } = useTranslation();
  const toast = useToast();

  const [scene, setScene] = useState("");
  const [rescript, setRescript] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<RescriptResponse | null>(null);
  const [presentOpen, setPresentOpen] = useState(false);

  const handleGenerate = async () => {
    try {
      setBusy(true);
      const res = await postRescript(getToken, {
        patient_id: patientId,
        session_id: sessionId,
        scene_prompt: scene,
        rescript_prompt: rescript,
      });
      setResult(res);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg.includes("image_policy_declined")) {
        toast.error(t("experiential.policyDeclined"));
      } else {
        toast.error(t("experiential.genError"));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <TextField
        label={t("experiential.scene")}
        multiline
        minRows={3}
        fullWidth
        size="small"
        value={scene}
        onChange={(e) => setScene(e.target.value)}
        sx={{ mb: 2 }}
      />
      <TextField
        label={t("experiential.rescript")}
        multiline
        minRows={3}
        fullWidth
        size="small"
        value={rescript}
        onChange={(e) => setRescript(e.target.value)}
        sx={{ mb: 2 }}
      />
      <Button
        variant="contained"
        size="small"
        onClick={handleGenerate}
        disabled={busy || !scene.trim() || !rescript.trim()}
        sx={{ mb: 2 }}
      >
        {t("experiential.generateImages")}
      </Button>

      {result && (
        <>
          <div className="exp-rescript-grid">
            <div>
              <div style={{ marginBottom: 4, fontWeight: 500, fontSize: 13 }}>
                {t("experiential.before")}
              </div>
              <img
                src={result.before_url}
                alt={t("experiential.before")}
                style={{ width: "100%", borderRadius: 6 }}
              />
            </div>
            <div>
              <div style={{ marginBottom: 4, fontWeight: 500, fontSize: 13 }}>
                {t("experiential.after")}
              </div>
              <img
                src={result.after_url}
                alt={t("experiential.after")}
                style={{ width: "100%", borderRadius: 6 }}
              />
            </div>
          </div>

          <Button
            variant="outlined"
            size="small"
            onClick={() => setPresentOpen(true)}
            sx={{ mt: 2 }}
          >
            {t("experiential.present")}
          </Button>

          <Dialog
            open={presentOpen}
            onClose={() => setPresentOpen(false)}
            maxWidth="xl"
            fullWidth
          >
            <DialogContent sx={{ p: 0 }}>
              <img
                src={result.after_url}
                alt={t("experiential.after")}
                className="exp-present-img"
              />
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
}
